import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FilterTab } from "components/tab-filter/tab-filter.props";
import { EqualFilter, Filter } from "libs/models/filter";
import {
  InvoiceModel,
  InvoicePaymentRequest,
  InvoiceProductModel,
} from "libs/models/invoice";
import { PaginatedList } from "libs/models/paginated-list";
import { invoicesTabFilters } from "pages/InvoicesView/Invoices-view.util";
import { AsyncAppThunk } from "store/application-state";
import { addSnack, showLoaderWhile } from "./ui";
import { getFilterQuery } from "utils/filter";
import {
  downloadFileByIdService,
  generateInvoiceService,
  getInvoiceDetailsByIdService,
  getInvoiceProductsService,
  getInvoicesService,
  getInvoiceTransactionsService,
  payInvoiceService,
} from "services/crosspay-service";
import { TransactionsData } from "libs/models/transaction";
import moment from "moment";
import { invoiceDetailsFilterTabs } from "pages/InvoiceDetailsView/invoice-details.util";
import { validateInvoicePaymentState } from "components/invoice-payment/invoice-payment.policy";
import { setCloseDialog } from "./modal-popup";
import { CustomizedPush } from "store/navigation-wrapper";
import { CROSSPAY_ROUTES } from "libs/routes";
import translate from "utils/translations";

export interface InvoicesStore {
  invoicesData: PaginatedList<InvoiceModel>;
  invoiceDetails: InvoiceModel;
  tabFilterValue: FilterTab;
  invoiceDetailsTabFilterValue: FilterTab;
  searchingValue: string | undefined;
  filter: Filter;
  invoiceTransactionsFilter: Filter;
  invoiceProductsFilter: Filter;
  invoiceTransactions: PaginatedList<TransactionsData>;
  invoiceProducts: PaginatedList<InvoiceProductModel>;
  paymentRequest: InvoicePaymentRequest;
  isValidating: boolean;
  dataLoaded: boolean;
}
const initialState: InvoicesStore = {
  invoicesData: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  invoiceDetails: {} as InvoiceModel,
  paymentRequest: {} as InvoicePaymentRequest,
  searchingValue: undefined,
  tabFilterValue: invoicesTabFilters[0],
  invoiceDetailsTabFilterValue: invoiceDetailsFilterTabs[0],
  filter: {
    orderBy: { key: "id", sort: "asc" },
    issuedAt: { ge: new Date(), le: new Date() },
  },
  invoiceTransactionsFilter: {
    orderBy: { key: "id", sort: "asc" },
  },
  invoiceProductsFilter: {
    orderBy: { key: "id", sort: "asc" },
  },
  invoiceTransactions: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  invoiceProducts: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  isValidating: false,
  dataLoaded: false,
};
const slice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    setInvoicesFetchingData: (
      state: InvoicesStore,
      action: PayloadAction<PaginatedList<InvoiceModel>>
    ) => {
      state.invoicesData = action.payload;
    },
    setInvoiceTransactionsFetchingData: (
      state: InvoicesStore,
      action: PayloadAction<PaginatedList<TransactionsData>>
    ) => {
      state.invoiceTransactions = action.payload;
    },
    setEqualFilter: (
      state: InvoicesStore,
      action: PayloadAction<{ key: string; value?: any }>
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.filter[key];
        return state;
      } else {
        state.filter = { ...state.filter, [key]: value! };
      }
      return state;
    },
    setFilterDate: (
      state: InvoicesStore,
      action: PayloadAction<{ from: Date; to: Date }>
    ) => {
      const startDate = new Date(action.payload.from);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(action.payload.to);
      endDate.setHours(23, 59, 59, 999);
      const customDateFilter = {
        issuedAt: {
          ge: new Date(startDate),
          le: new Date(endDate),
        },
      };
      state.filter = { ...state.filter, ...customDateFilter };
    },
    setTabFilterChange: (
      state: InvoicesStore,
      action: PayloadAction<FilterTab>
    ) => {
      state.tabFilterValue = action.payload;
    },
    setInvoiceDetailsTabFilterChange: (
      state: InvoicesStore,
      action: PayloadAction<FilterTab>
    ) => {
      state.invoiceDetailsTabFilterValue = action.payload;
    },
    setInvoiceViewDynamicSearchFilter: (
      state: InvoicesStore,
      action: PayloadAction<EqualFilter[]>
    ) => {
      state.filter = { ...state.filter, or: action.payload };
    },
    setSearchKey: (state: InvoicesStore, action: PayloadAction<string>) => {
      state.searchingValue = action.payload;
    },
    setInvoicesPage: (state: InvoicesStore, action: PayloadAction<number>) => {
      state.invoicesData.pageIndex = action.payload;
    },
    setInvoiceDetails: (
      state: InvoicesStore,
      action: PayloadAction<InvoiceModel>
    ) => {
      state.invoiceDetails = action.payload;
    },
    setInvoiceProductsFetchingData: (
      state: InvoicesStore,
      action: PayloadAction<PaginatedList<InvoiceProductModel>>
    ) => {
      state.invoiceProducts = action.payload;
    },
    setInvoiceTransactionsPage: (
      state: InvoicesStore,
      action: PayloadAction<number>
    ) => {
      state.invoiceTransactions.pageIndex = action.payload;
    },
    setInvoiceProductsPage: (
      state: InvoicesStore,
      action: PayloadAction<number>
    ) => {
      state.invoiceProducts.pageIndex = action.payload;
    },
    setInvoicePaymentRequestData: (
      state: InvoicesStore,
      action: PayloadAction<{ key: string; value?: any }>
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        paymentRequest: { ...state.paymentRequest, [key]: value! },
      };
    },
    setIsValidating: (state: InvoicesStore, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    clearInvoicePaymentRequestData: (state: InvoicesStore) => {
      state.paymentRequest = initialState.paymentRequest;
    },
    setDataLoaded: (state: InvoicesStore, action: PayloadAction<boolean>) => {
      state.dataLoaded = action.payload;
    },
    clearFilters: (state: InvoicesStore) => {
      state.filter = initialState.filter;
      state.searchingValue = undefined;
    },
  },
});
export const {
  setInvoicesFetchingData,
  setEqualFilter,
  setTabFilterChange,
  setInvoiceViewDynamicSearchFilter,
  setSearchKey,
  setInvoicesPage,
  setInvoiceTransactionsFetchingData,
  setFilterDate,
  setInvoiceDetails,
  setInvoiceProductsFetchingData,
  setInvoiceDetailsTabFilterChange,
  setInvoiceTransactionsPage,
  setInvoiceProductsPage,
  setInvoicePaymentRequestData,
  setIsValidating,
  clearInvoicePaymentRequestData,
  setDataLoaded,
  clearFilters,
} = slice.actions;

export const reducer = slice.reducer;

export const getInvoicesData = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(
    showLoaderWhile(async () => {
      var invoicesPaging = store().invoices.invoicesData;
      var queryOptions = getFilterQuery(
        store().invoices.filter,
        invoicesPaging.pageIndex * invoicesPaging.pageSize,
        invoicesPaging.pageSize
      );
      const apiResponse = await getInvoicesService(queryOptions);
      if (
        apiResponse &&
        Object.keys(apiResponse).length > 0 &&
        apiResponse.result !== null
      ) {
        dispatch(setInvoicesFetchingData(apiResponse.result));
        dispatch(setDataLoaded(true));
      }
    })
  );
};

export const getInvoiceTransactionsData =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        var invoiceTransactionsPaging = store().invoices.invoiceTransactions;
        var queryOptions = getFilterQuery(
          {
            ...store().invoices.invoiceTransactionsFilter,
            transactionType: "Purchase",
          },
          invoiceTransactionsPaging.pageIndex *
            invoiceTransactionsPaging.pageSize,
          invoiceTransactionsPaging.pageSize
        );
        const apiResponse = await getInvoiceTransactionsService(
          id,
          queryOptions
        );
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setInvoiceTransactionsFetchingData(apiResponse.result));
        }
      })
    );
  };
export const getInvoiceDetailsById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getInvoiceDetailsByIdService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setInvoiceDetails(apiResponse.result));
        }
      })
    );
  };
export const generateInvoice =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await generateInvoiceService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null &&
          apiResponse.errorCode === null
        ) {
          dispatch(
            addSnack(
              translate(
                "Invoice_Generating_Text",
                "The Invoice is being generating, We'll email you when it's done"
              )
            )
          );
        }
      })
    );
  };
export const getInvoiceProductsData =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        var invoiceTransactionsPaging = store().invoices.invoiceProducts;
        var queryOptions = getFilterQuery(
          store().invoices.invoiceProductsFilter,
          invoiceTransactionsPaging.pageIndex *
            invoiceTransactionsPaging.pageSize,
          invoiceTransactionsPaging.pageSize
        );
        const apiResponse = await getInvoiceProductsService(id, queryOptions);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setInvoiceProductsFetchingData(apiResponse.result));
        }
      })
    );
  };
export const downloadInvoiceFileById =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await downloadFileByIdService(id);
        if (apiResponse && Object.keys(apiResponse).length > 0) {
          dispatch(
            addSnack({
              message: translate(
                "File_Downloaded_Text",
                "File Downloaded successfully"
              ),
            })
          );
        }
      })
    );
  };

export const payInvoice = (): AsyncAppThunk => async (dispatch, store) => {
  var invoiceState = store().invoices;
  const paymentRequest = {
    ...invoiceState.paymentRequest,
    invoiceId: invoiceState.invoiceDetails.id,
    paymentDate:
      invoiceState.paymentRequest.paymentDate ?? moment().toISOString(),
  };
  dispatch(setIsValidating(true));
  if (Object.keys(validateInvoicePaymentState(store().invoices)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        const response = await payInvoiceService(paymentRequest);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          dispatch(clearInvoicePaymentRequestData());
          dispatch(setCloseDialog());
          CustomizedPush(CROSSPAY_ROUTES.INVOICING);
        }
      })
    );
  }
};
